﻿import { useEffect, useMemo, useState } from 'react';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './auth-context';

export type ABWeekPattern = '' | 'A' | 'B';

export interface ABWeekCalendarDoc {
  weekADatesText: string;
  weekBDatesText: string;
  updatedAt: Timestamp | FieldValue | null;
  updatedBy: string;
}

export interface ABWeekCalendarLookup {
  weekAWeekStarts: Set<string>;
  weekBWeekStarts: Set<string>;
  hasConfiguredWeeks: boolean;
}

export interface UseABWeekCalendarResult {
  loading: boolean;
  calendar: ABWeekCalendarDoc;
  lookup: ABWeekCalendarLookup;
  error: string | null;
  /**
   * True until Firestore confirms this listener from the server (or the
   * hook is disabled). Starts true and only a real server snapshot clears
   * it - same posture and same reason as useAllClassSeries' fromCache in
   * lib/classSeries.ts: `loading` alone goes false the instant a CACHED
   * first snapshot arrives, which reads as "confirmed" when it may really
   * be stale offline data. A caller doing its own honest-state gating (see
   * components/SocialMediaReminderCard.tsx) needs this to tell "no A/B
   * calendar configured" from "have not heard from the server yet."
   */
  fromCache: boolean;
}

const EMPTY_CALENDAR: ABWeekCalendarDoc = {
  weekADatesText: '',
  weekBDatesText: '',
  updatedAt: null,
  updatedBy: '',
};

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

export function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function isValidDateParts(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function normalizeYear(value: string): number {
  const year = Number(value);
  if (value.length === 2) return year >= 70 ? 1900 + year : 2000 + year;
  return year;
}

export function parseDateListLine(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const iso = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(trimmed);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    return isValidDateParts(year, month, day) ? `${year}-${pad2(month)}-${pad2(day)}` : null;
  }

  const slash = /\b(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})\b/.exec(trimmed);
  if (slash) {
    const month = Number(slash[1]);
    const day = Number(slash[2]);
    const year = normalizeYear(slash[3]);
    return isValidDateParts(year, month, day) ? `${year}-${pad2(month)}-${pad2(day)}` : null;
  }

  return null;
}

export function weekStartMondayKey(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  const weekday = date.getDay();
  date.setDate(date.getDate() + (weekday === 0 ? -6 : 1 - weekday));
  return localDateKey(date);
}

export function parseWeekCalendarText(text: string): string[] {
  const seen = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const dateKey = parseDateListLine(line);
    if (!dateKey) continue;
    seen.add(weekStartMondayKey(dateKey));
  }
  return [...seen].sort();
}

function normalizeCalendar(raw: unknown): ABWeekCalendarDoc {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    weekADatesText: typeof data.weekADatesText === 'string' ? data.weekADatesText : '',
    weekBDatesText: typeof data.weekBDatesText === 'string' ? data.weekBDatesText : '',
    updatedAt: (data.updatedAt as Timestamp | FieldValue | null) ?? null,
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
}

export function buildABWeekCalendarLookup(calendar: Pick<ABWeekCalendarDoc, 'weekADatesText' | 'weekBDatesText'>): ABWeekCalendarLookup {
  const weekAWeekStarts = new Set(parseWeekCalendarText(calendar.weekADatesText));
  const weekBWeekStarts = new Set(parseWeekCalendarText(calendar.weekBDatesText));
  return {
    weekAWeekStarts,
    weekBWeekStarts,
    hasConfiguredWeeks: weekAWeekStarts.size > 0 || weekBWeekStarts.size > 0,
  };
}

/**
 * enabled exists from when studioSettings was a staff-only read across the
 * board and a family mount was guaranteed permission-denied. The
 * abWeekCalendar doc itself became an any-active-user read on 2026-08-19
 * (the family calendar's A/B markers - see shared/firestore.rules), so an
 * active FAMILY viewer may now mount this hook enabled. The flag is still
 * worth passing where a surface only needs the data conditionally (the
 * class page schedule panel passes isStaff && expanded), and a dancer-login
 * session (status 'dancer', not 'active') is still denied - dancer surfaces
 * get their data through the server portal, never this listener. Disabled
 * returns the empty calendar with loading false, same as every other gated
 * hook in this app.
 */
export function useABWeekCalendar(enabled = true): UseABWeekCalendarResult {
  const [calendar, setCalendar] = useState<ABWeekCalendarDoc>(EMPTY_CALENDAR);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!db || !enabled) {
      setCalendar(EMPTY_CALENDAR);
      setLoading(false);
      setFromCache(false);
      return;
    }
    // Back to true on every enabled-flip. The schedule panel mounts this as
    // useABWeekCalendar(isStaff && expanded), so enabled flips on the frame a
    // staff member expands a class. Left false, hasConfiguredWeeks reads false
    // while settled, and the closed-row builder falls back to
    // alternate-weeks-from-termStart and paints closures on weeks the class
    // never meets, then rearranges when the snapshot lands.
    setLoading(true);
    setFromCache(true);
    const unsub = onSnapshot(
      doc(db, 'studioSettings', 'abWeekCalendar'),
      // includeMetadataChanges so a cache-then-server pair with IDENTICAL
      // data still re-fires this callback purely to flip fromCache false -
      // same reasoning and same option lib/closures.ts's useClosures uses.
      { includeMetadataChanges: true },
      (snap) => {
        setCalendar(snap.exists() ? normalizeCalendar(snap.data()) : EMPTY_CALENDAR);
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled]);

  const lookup = useMemo(() => buildABWeekCalendarLookup(calendar), [calendar]);

  return { loading, calendar, lookup, error, fromCache };
}

/**
 * The letters that would be wiped rather than reconciled. Only letters that
 * actually have classes count: a studio that runs no B-week classes and
 * lists no B weeks is not half-filled, it is consistent.
 *
 * An A/B calendar with nothing in it at all is not half-filled either -
 * classSeriesRunsOnDate falls back to alternating from each series' own
 * term start in that case, which is a real answer, not a blank one.
 */
export function lettersMissingFromCalendar(
  letters: readonly ('A' | 'B')[],
  lookup: ABWeekCalendarLookup
): ('A' | 'B')[] {
  if (!lookup.hasConfiguredWeeks) return [];
  const out: ('A' | 'B')[] = [];
  if (letters.includes('A') && lookup.weekAWeekStarts.size === 0) out.push('A');
  if (letters.includes('B') && lookup.weekBWeekStarts.size === 0) out.push('B');
  return out;
}

/**
 * Weeks that appear under BOTH letters, which is a contradiction that
 * silently deletes classes.
 *
 * Bobbie, 2026-09-03, after saving 8 A weeks and 7 B weeks and watching a
 * B-week class stay off the calendar entirely: "I did the edit on a/b it did
 * not fix it."
 *
 * PROVEN BY RUNNING IT, not reasoned about. weekPatternForDateKey below asks
 * the A set first and returns on a hit, so a week listed under both letters
 * answers 'A' and every B class in that week matches nothing and books
 * NOTHING. No error is raised anywhere: the lists save, the counts look
 * healthy ("8 A weeks saved, 7 B weeks saved"), and the classes just are not
 * there.
 *
 * THE COMMONEST WAY IN IS A SUNDAY. A week here runs Monday through Sunday,
 * so a Sunday date belongs to the week that began the PREVIOUS Monday. Paste
 * Sundays in one box and Mondays in the other and the two lists land on the
 * same weeks, every time, with nothing on screen to say so.
 *
 * PURE. Returns the overlapping week-start Mondays, sorted.
 */
export function overlappingABWeeks(lookup: Pick<ABWeekCalendarLookup, 'weekAWeekStarts' | 'weekBWeekStarts'>): string[] {
  const out: string[] = [];
  for (const weekStart of lookup.weekAWeekStarts) {
    if (lookup.weekBWeekStarts.has(weekStart)) out.push(weekStart);
  }
  return out.sort();
}

/**
 * PURE. The dates in a list that are Sundays, which are the likeliest cause
 * of an overlap and the one a person cannot see by reading their own list.
 */
export function sundayDatesInList(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const key = parseDateListLine(line);
    if (!key) continue;
    const [y, m, d] = key.split('-').map(Number);
    if (new Date(y, m - 1, d).getDay() === 0) out.push(key);
  }
  return out;
}

/**
 * PURE. The Sundays in a list that actually land on one of the overlapping
 * weeks, which are the only ones worth telling a Director to move.
 *
 * Caught in review: the first cut picked whichever list happened to contain
 * any Sunday at all, preferring B. So an unrelated, perfectly valid Sunday in
 * the B list would be blamed for an overlap caused by a Sunday in the A list,
 * and following the advice would shift a good B week while leaving the real
 * contradiction untouched. Attribute the advice to the dates that caused it,
 * or do not give it.
 */
export function sundaysInOverlappingWeeks(
  text: string,
  overlapWeekStarts: readonly string[]
): string[] {
  if (overlapWeekStarts.length === 0) return [];
  const overlapping = new Set(overlapWeekStarts);
  return sundayDatesInList(text).filter((dateKey) => overlapping.has(weekStartMondayKey(dateKey)));
}

export function weekPatternForDateKey(dateKey: string, lookup: ABWeekCalendarLookup): 'A' | 'B' | null {
  if (!lookup.hasConfiguredWeeks) return null;
  const weekStart = weekStartMondayKey(dateKey);
  if (lookup.weekAWeekStarts.has(weekStart)) return 'A';
  if (lookup.weekBWeekStarts.has(weekStart)) return 'B';
  return null;
}

/**
 * The single letter a CLASS may honestly wear ("this class practices on A
 * weeks"), derived from its actual upcoming meeting dates rather than any
 * schedule metadata a family cannot read. Every date must land on a marked
 * week and every one on the SAME letter; an every-week class hits both A
 * and B weeks and correctly gets no letter, and an unmarked calendar gets
 * none either. Empty input is null: no meetings is not evidence of a
 * pattern.
 */
export function uniformWeekPattern(
  dateKeys: readonly string[],
  lookup: ABWeekCalendarLookup
): 'A' | 'B' | null {
  if (dateKeys.length === 0 || !lookup.hasConfiguredWeeks) return null;
  let letter: 'A' | 'B' | null = null;
  for (const dateKey of dateKeys) {
    const pattern = weekPatternForDateKey(dateKey, lookup);
    if (!pattern) return null;
    if (letter === null) letter = pattern;
    else if (letter !== pattern) return null;
  }
  return letter;
}

/**
 * Every marked week from this Monday forward, oldest first, for the
 * Important dates page's A/B weeks list (Bobbie, 2026-08-19: "Under
 * important dates - there should be the list of a/b weeks as well").
 * Weeks already fully in the past are left out - the list answers "which
 * week is coming", not "what the season was". Pure so it is testable.
 */
export function upcomingMarkedWeeks(
  lookup: ABWeekCalendarLookup,
  todayKey: string
): { weekStart: string; letter: 'A' | 'B' }[] {
  const thisWeekStart = weekStartMondayKey(todayKey);
  const rows: { weekStart: string; letter: 'A' | 'B' }[] = [];
  for (const weekStart of lookup.weekAWeekStarts) {
    if (weekStart >= thisWeekStart) rows.push({ weekStart, letter: 'A' });
  }
  for (const weekStart of lookup.weekBWeekStarts) {
    // A week pasted into BOTH settings boxes resolves A-first, the same
    // precedence weekPatternForDateKey already applies, so this list and
    // the calendar chips never disagree and React never sees a duplicate
    // key (delta review, 2026-08-19).
    if (lookup.weekAWeekStarts.has(weekStart)) continue;
    if (weekStart >= thisWeekStart) rows.push({ weekStart, letter: 'B' });
  }
  rows.sort((left, right) => left.weekStart.localeCompare(right.weekStart));
  return rows;
}

/**
 * The single A/B letter a class card may honestly wear from its SERIES'
 * own weekPattern field ('' | 'A' | 'B', lib/classSeries.ts), rather than
 * from any reservation date. This is the stronger signal where it exists:
 * a Director sets it directly when creating the class ("this class
 * practices on A weeks"), so it needs no A/B calendar lookup at all - it is
 * true regardless of whether any date has been marked yet.
 *
 * Callers pass ONLY the patterns of the series currently in force for one
 * offering (lib/teachSchedule.ts's seriesInForce picks which term), already
 * run through normalizeClassSeriesWeekPattern. A class meeting twice a week
 * on two DIFFERENT patterns, or with even one every-week ('') slot among
 * its in-force series, has no single honest letter - same "no letter beats
 * a wrong letter" rule uniformWeekPattern already follows for dates - so
 * disagreement or an empty pattern returns null rather than guessing.
 */
export function classSeriesWeekLetter(
  patterns: readonly ABWeekPattern[]
): 'A' | 'B' | null {
  if (patterns.length === 0) return null;
  let letter: 'A' | 'B' | null = null;
  for (const pattern of patterns) {
    if (pattern !== 'A' && pattern !== 'B') return null;
    if (letter === null) letter = pattern;
    else if (letter !== pattern) return null;
  }
  return letter;
}

export function countConfiguredWeeks(calendar: Pick<ABWeekCalendarDoc, 'weekADatesText' | 'weekBDatesText'>): { weekA: number; weekB: number } {
  return {
    weekA: parseWeekCalendarText(calendar.weekADatesText).length,
    weekB: parseWeekCalendarText(calendar.weekBDatesText).length,
  };
}

export function useSaveABWeekCalendar() {
  const { user } = useAuth();
  return async (weekADatesText: string, weekBDatesText: string): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await setDoc(doc(db, 'studioSettings', 'abWeekCalendar'), {
      weekADatesText: weekADatesText.trim(),
      weekBDatesText: weekBDatesText.trim(),
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  };
}

export type WeekLetter = 'A' | 'B';

function otherWeekLetter(letter: WeekLetter): WeekLetter {
  return letter === 'A' ? 'B' : 'A';
}

/**
 * Whole-day timestamp for a date-key, computed in UTC. UTC never observes a
 * daylight-saving transition, so subtracting two of these always gives an
 * exact multiple of 86400000ms for any two date-keys that are a whole
 * number of days apart - unlike constructing local Date objects and
 * subtracting their getTime(), where a DST change between the two dates
 * would shift the raw millisecond difference by an hour and could round to
 * the wrong day count.
 */
function dateKeyToUTCTime(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

/**
 * Whole calendar days from date-key a to date-key b (b - a). See
 * dateKeyToUTCTime for why this is DST-safe.
 */
function daysBetweenDateKeys(a: string, b: string): number {
  return Math.round((dateKeyToUTCTime(b) - dateKeyToUTCTime(a)) / 86400000);
}

/**
 * Add a whole number of days to a date-key and return the new date-key.
 * Builds a local-midnight Date and advances it with setDate(), the same
 * technique weekStartMondayKey already uses above - setDate() operates on
 * the calendar day field, not the millisecond epoch, so a spring-forward or
 * fall-back transition crossed along the way never skips or repeats a day.
 * Local midnight is used only as a stable anchor to read back
 * year/month/day from; no time-of-day math happens here.
 */
function addDaysToDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

/**
 * The A/B letter for any Monday, given one known anchor Monday and its
 * letter. Weeks alternate strictly every 7 days in both directions from the
 * anchor, so this is exact whole-week arithmetic, not a lookup - and
 * because daysBetweenDateKeys works in UTC, a DST change anywhere between
 * the anchor and the target Monday can never shift which letter comes out.
 */
export function weekPatternFromAnchor(
  mondayKey: string,
  anchorMondayKey: string,
  anchorPattern: WeekLetter
): WeekLetter {
  const anchorMonday = weekStartMondayKey(anchorMondayKey);
  const monday = weekStartMondayKey(mondayKey);
  const weekDiff = daysBetweenDateKeys(anchorMonday, monday) / 7;
  const parity = ((Math.round(weekDiff) % 2) + 2) % 2;
  return parity === 0 ? anchorPattern : otherWeekLetter(anchorPattern);
}

/**
 * A default season end for the auto-fill tool: the next July 31 on or after
 * the anchor Monday. LDG's studio year runs roughly August/September
 * through June/July (Nationals sits in late June), so anchoring on a fall
 * rehearsal date and filling through the following July covers one whole
 * studio year in a single pass. If the anchor itself already falls after
 * July 31 of its own year, the default rolls to next year's July 31 instead
 * of proposing a season that ends before it starts.
 */
export function defaultSeasonEndDateKey(anchorMondayKey: string): string {
  const [year] = anchorMondayKey.split('-').map(Number);
  const sameYearJuly31 = `${year}-07-31`;
  if (daysBetweenDateKeys(anchorMondayKey, sameYearJuly31) >= 0) {
    return sameYearJuly31;
  }
  return `${year + 1}-07-31`;
}

export interface GenerateAlternatingWeeksInput {
  /** Any date in the "anchor" week - the week whose letter is known. */
  anchorMondayKey: string;
  /** The letter of the anchor's week. */
  anchorPattern: WeekLetter;
  /** Any date in the first week to fill; defaults to the anchor week. */
  seasonStartKey?: string;
  /** Any date in the last week to fill (inclusive). */
  seasonEndKey: string;
}

export interface GenerateAlternatingWeeksResult {
  weekADatesText: string;
  weekBDatesText: string;
  weekACount: number;
  weekBCount: number;
  weekCount: number;
}

/** ~11.5 years of weekly rehearsals - a generous safety cap, not a real limit. */
const MAX_GENERATED_WEEKS = 600;

/**
 * Bobbie: "we should be able to see A and B weeks for the year and they
 * will not change." A and B alternate strictly and forever once one week's
 * letter is known, so the Director only ever needs to say ONE thing: "this
 * week is A" (or B). This fills every Monday from the season start through
 * the season end (inclusive) with the letter that alternation implies,
 * writing straight into the same two newline-separated date-key lists
 * weekADatesText/weekBDatesText that the manual paste-boxes and the
 * calendar's tap-to-mark tool already read and write - so a Director can
 * still hand-correct a single odd week afterward with either of those, on
 * top of whatever this generates.
 *
 * Pure and deterministic: same inputs always produce the same two lists,
 * marching Monday to Monday with addDaysToDateKey/weekPatternFromAnchor,
 * both of which are DST-safe by construction (see their own comments).
 */
/**
 * NO SCREEN CALLS THIS ANY MORE, since 2026-08-21. The Auto-fill A/B card in
 * Studio Settings was its only caller, and Bobbie removed that card ("Remove
 * the x section keep the stared"). Kept, with its tests, because the logic is
 * correct and the season ritual may want it again; recorded here so nobody
 * reads it as live and reasons from that. If it is still unused when the next
 * season is set up, delete it and its tests together.
 */
export function generateAlternatingWeekCalendar(
  input: GenerateAlternatingWeeksInput
): GenerateAlternatingWeeksResult {
  const anchorMonday = weekStartMondayKey(input.anchorMondayKey);
  const startMonday = weekStartMondayKey(input.seasonStartKey ?? input.anchorMondayKey);
  const endMonday = weekStartMondayKey(input.seasonEndKey);

  const weekA: string[] = [];
  const weekB: string[] = [];

  if (daysBetweenDateKeys(startMonday, endMonday) >= 0) {
    let current = startMonday;
    let count = 0;
    while (daysBetweenDateKeys(current, endMonday) >= 0 && count < MAX_GENERATED_WEEKS) {
      const pattern = weekPatternFromAnchor(current, anchorMonday, input.anchorPattern);
      (pattern === 'A' ? weekA : weekB).push(current);
      current = addDaysToDateKey(current, 7);
      count += 1;
    }
  }

  return {
    weekADatesText: weekA.join('\n'),
    weekBDatesText: weekB.join('\n'),
    weekACount: weekA.length,
    weekBCount: weekB.length,
    weekCount: weekA.length + weekB.length,
  };
}

/**
 * Recompute the A/B calendar text after moving ONE week to a new pattern.
 * The week is removed from whichever list it was already in (if any) so it
 * can never appear in both, then added to the requested one. Pure function
 * so the calendar's tap-to-mark control and the write path below share
 * identical logic - no drift between what a Director sees previewed and
 * what actually gets saved.
 */
export function computeWeekPatternUpdate(
  calendar: Pick<ABWeekCalendarDoc, 'weekADatesText' | 'weekBDatesText'>,
  weekStartKey: string,
  pattern: 'A' | 'B'
): { weekADatesText: string; weekBDatesText: string } {
  const weekAStarts = new Set(parseWeekCalendarText(calendar.weekADatesText));
  const weekBStarts = new Set(parseWeekCalendarText(calendar.weekBDatesText));
  weekAStarts.delete(weekStartKey);
  weekBStarts.delete(weekStartKey);
  (pattern === 'A' ? weekAStarts : weekBStarts).add(weekStartKey);
  return {
    weekADatesText: [...weekAStarts].sort().join('\n'),
    weekBDatesText: [...weekBStarts].sort().join('\n'),
  };
}

/**
 * Hook returning a function that marks a single week A or B directly from
 * the calendar - Bobbie: "we should just be able to add those into the
 * calendar ... quickly add a week and b week on the calendar". This writes
 * the exact same studioSettings/abWeekCalendar doc the Studio Settings
 * paste-boxes write (useSaveABWeekCalendar above); both are just different
 * front ends onto the same two text fields, so either UI always sees the
 * other's changes on its next read.
 */
export function useSetWeekPattern() {
  const { user } = useAuth();
  return async (
    calendar: Pick<ABWeekCalendarDoc, 'weekADatesText' | 'weekBDatesText'>,
    weekStartKey: string,
    pattern: 'A' | 'B'
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const next = computeWeekPatternUpdate(calendar, weekStartKey, pattern);
    await setDoc(doc(db, 'studioSettings', 'abWeekCalendar'), {
      weekADatesText: next.weekADatesText,
      weekBDatesText: next.weekBDatesText,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    });
  };
}
